import { useEffect, useState } from "react";
import { Form, Col, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import {
  CheckCircleOutlined,
  CloseOutlined,
  UnorderedListOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import useDelete from "../../../global/hooks/useDelete";
import ConfirmModal from "../../../global/components/ConfirmModal";
import { FaFileInvoiceDollar } from "react-icons/fa6";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { PrepurchasedLicenseItemsColumns } from "./PrepurchasedLicenseItemsColumns";
import PrepurchasedLicenseItemModal from "./PrepurchasedLicenseItemModal";
import { getGiftCardRowId } from "../utils/rowId";
import { getPrepurchasedLicenseListPath } from "../utils/listSearchStorage";

const GIFT_CARDS_BASE = `${process.env.REACT_APP_API_URL}/api/giftcards`;

export const PrepurchasedLicenseForm = ({ type, onCancel }) => {
  const { id } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [showItemsDeleteModal, setShowItemsDeleteModal] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemNo, setEditingItemNo] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const url = id && `${GIFT_CARDS_BASE}/${id}/`;
  const postUrl = `${GIFT_CARDS_BASE}/`;
  const putUrl = `${GIFT_CARDS_BASE}/${id}/`;
  const deleteUrl = `${GIFT_CARDS_BASE}/${id}/`;
  const itemsUrl = id ? `${GIFT_CARDS_BASE}/${id}/items/` : null;
  const deleteItemsUrl = id
    ? `${GIFT_CARDS_BASE}/${id}/items/bulk-delete/`
    : `${GIFT_CARDS_BASE}/0/items/bulk-delete/`;

  const { data, loading, refetch: giftCardRefetch } = useGet(url);
  const {
    data: itemsData,
    loading: itemsLoading,
    refetch: itemsRefetch,
  } = useGet(itemsUrl);

  const { error: postError, postData: postAction } = usePost(postUrl);
  const { error: putError, putData: putAction } = usePut(putUrl);
  const {
    data: deletedData,
    error: deletedDataError,
    deleteData,
  } = useDelete(deleteUrl);
  const {
    data: deletedItemsData,
    error: deletedItemsDataError,
    deleteMultiple: deleteItemsData,
  } = useDelete(deleteItemsUrl);

  const onFinish = async (values) => {
    try {
      const modifiedValue = {
        ...values,
        Price: values.Price
          ? Number(parseFloat(values.Price).toFixed(2))
          : values.Price,
      };
      if (id) {
        const result = await putAction(modifiedValue);
        if (result != null) {
          await Promise.all([giftCardRefetch(), itemsRefetch?.()]);
          openConfirmNotification("Success");
          setTimeout(() => {
            navigate(`/prepurchased-license/info/${id}`);
          }, 200);
        }
      } else {
        const result = await postAction(modifiedValue);
        if (result != null) {
          openConfirmNotification("Success");
          setTimeout(() => {
            const rid = getGiftCardRowId(result?.data ?? result);
            navigate(
              rid != null
                ? `/prepurchased-license/info/${rid}`
                : getPrepurchasedLicenseListPath(),
            );
          }, 200);
        }
      }
    } catch (err) {
      openErrorNotification("Something went wrong.");
      console.log(err);
    }
  };

  const onReset = () => {
    onCancel ? onCancel() : navigate(-1);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (err) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };

  const onDeleteItemsConfirm = async () => {
    try {
      await deleteItemsData({ ids: selectedRowKeys });
    } catch (err) {
      openErrorNotification("Something went wrong.");
    }
    setShowItemsDeleteModal(false);
    setSelectedRowKeys([]);
  };

  const onSelectItemRowsChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const itemRowSelection = {
    selectedRowKeys,
    onChange: onSelectItemRowsChange,
    fixed: true,
  };

  const itemButtonsData = [
    {
      label: "Add",
      icon: <PlusOutlined />,
      onClick: () => {
        setEditingItem(null);
        setEditingItemNo(null);
        setItemModalOpen(true);
      },
    },
    {
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: () => {
        setShowItemsDeleteModal(true);
      },
      disabled: selectedRowKeys?.length === 0,
    },
  ];

  useEffect(() => {
    if (id) return;
    form.setFieldsValue({ Price: 0 });
  }, [id, form]);

  useEffect(() => {
    if (!id || loading) return;
    const load = () => {
      try {
        form.resetFields();
        if (data?.data) {
          const d = data.data;
          const priceVal = d.Price ?? d.Amount;
          form.setFieldsValue({
            GiftCardName: String(d.GiftCardName ?? ""),
            GiftCardDesc: d.GiftCardDesc ? String(d.GiftCardDesc) : "",
            Price: priceVal != null && priceVal !== "" ? priceVal : "",
          });
        }
      } catch (err) {
        if (!loading) {
          openErrorNotification("Error fetching data!");
        }
      }
    };

    load();
  }, [id, data, loading, form, type]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [postError]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [putError]);

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      navigate("/prepurchased-license");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError, navigate]);

  useEffect(() => {
    if (deletedItemsData && !deletedItemsDataError) {
      openConfirmNotification("Success");
      itemsRefetch();
      giftCardRefetch();
    } else if (deletedItemsDataError) {
      openErrorNotification(
        deletedItemsDataError?.response?.data?.message ||
          "Something went wrong",
      );
    }
  }, [
    deletedItemsData,
    deletedItemsDataError,
    itemsRefetch,
    giftCardRefetch,
  ]);

  const breadcrumbItems = [
    {
      label: "Prepurchased License",
      href: "/prepurchased-license",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/prepurchased-license/info/${id}`
          : `/prepurchased-license/edit/${id}`
        : "/prepurchased-license/add",
    },
  ];

  const buttonsData =
    type === "info"
      ? [
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => {
              navigate(`/prepurchased-license/edit/${id}`);
            },
          },
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => {
              setShowModal(true);
            },
          },
          {
            label: "List",
            icon: <UnorderedListOutlined />,
            onClick: () => {
              navigate(getPrepurchasedLicenseListPath());
            },
          },
        ]
      : [
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
            disabled: data?.data?.Paid === 1,
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ];

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>

          <div className={`bg-white overflow-hidden border rounded-[4px]`}>
            <div className="flex items-center bg-gray-200 p-2.5">
              <FaFileInvoiceDollar className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2 ">
                Prepurchased License
              </span>
            </div>

            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Name"
                    name="GiftCardName"
                    disabled={type === "info"}
                    required
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Description"
                    name="GiftCardDesc"
                    disabled={type === "info"}
                    loading={loading}
                  />
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Price"
                    name="Price"
                    disabled={type === "info"}
                    type="number"
                    required
                    prefix={"$"}
                    style={{ height: "30px" }}
                    loading={loading}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Form>

      {id ? (
        <>
          <div className="flex justify-end mb-2">
            <CommonButton buttonsData={itemButtonsData} />
          </div>
          <GlobalTable
            columns={PrepurchasedLicenseItemsColumns((record) => {
              setEditingItemNo(record?.ItemNo ?? null);
              setEditingItem(record);
              setItemModalOpen(true);
            })}
            dataSource={
              itemsData?.data
                ?.map((item) => ({
                  ...item,
                  key: item.ItemNo,
                }))
                ?.sort((a, b) => b.ItemNo - a.ItemNo) ?? []
            }
            rowSelection={itemRowSelection}
            onRow={(record) => ({
              onClick: (event) => {
                const clickedSelectionControl =
                  event.target.closest(".ant-table-selection-column") ||
                  event.target.closest(".ant-checkbox-wrapper") ||
                  event.target.closest(".ant-checkbox-input");
                if (clickedSelectionControl) return;
                setEditingItemNo(record?.ItemNo ?? null);
                setEditingItem(record);
                setItemModalOpen(true);
              },
              style: { cursor: "pointer" },
            })}
            loading={itemsLoading}
          />
          <PrepurchasedLicenseItemModal
            open={itemModalOpen}
            setOpen={setItemModalOpen}
            giftCardId={id}
            editingItem={editingItem}
            editingItemNo={editingItemNo}
            existingItems={itemsData?.data ?? []}
            setEditingItem={setEditingItem}
            itemsRefetch={() => {
              itemsRefetch();
              giftCardRefetch();
            }}
          />
          <ConfirmModal
            showModal={showItemsDeleteModal}
            setShowModal={setShowItemsDeleteModal}
            onDeleteConfirm={onDeleteItemsConfirm}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        </>
      ) : null}

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
    </>
  );
};
